import { alpha, lighten, type Theme } from "@mui/material/styles";
import { Box, Chip, Stack, Typography } from "../../ds";
import type { ChipColorVariant } from "../../ds";

interface Badge {
  label: string;
  tone: ChipColorVariant;
}

interface Props {
  total: number;
  passMark: number;
  outcome: Badge;
  /** Reward band once the score earns one, or the below-pass-mark flag. */
  badge?: Badge | null;
  /** What clearing the pass mark actually buys — editor only. */
  note?: string;
  /** Hold the strip at the top of the card while the sheet scrolls. */
  sticky?: boolean;
}

const SCALE = 100;
// Fixed columns so WC and SC strips align (see the grid below): wide
// enough for "100 / 100" at 32px, and for "Not Recommend" beside
// "Reward Category: A" / "Below pass mark".
const SCORE_COLUMN = 124;
const CHIP_COLUMN = 250;

const TINTABLE = ["success", "warning", "error", "info", "primary"] as const;

/**
 * Dark mode only: a filled semantic chip paints the lifted `main` under
 * dark text, a bright slab on the near-black card. Tint it the way
 * StatusChip's dark triples are — a wash of `main` under a light tint.
 */
function darkTint(tone: ChipColorVariant) {
  return (t: Theme) => {
    const slot = (TINTABLE as readonly string[]).includes(tone)
      ? t.palette[tone as (typeof TINTABLE)[number]]
      : null;
    if (t.palette.mode !== "dark" || !slot) return {};
    const text = lighten(slot.main, 0.7);
    return {
      bgcolor: alpha(slot.main, 0.16),
      color: text,
      "& .MuiChip-label": { color: text },
    };
  };
}

/**
 * The running total, the pass mark, and what the two together decide —
 * the foot of a paper annexure pulled to the head of the card, so the
 * consequence of a mark is visible while the mark is being entered.
 */
export default function ScoreStrip({
  total,
  passMark,
  outcome,
  badge,
  note,
  sticky = false,
}: Props) {
  const filled = Math.max(0, Math.min(100, (total / SCALE) * 100));
  const passAt = Math.max(0, Math.min(100, (passMark / SCALE) * 100));
  const passed = total >= passMark;

  return (
    <Box
      sx={{
        ...(sticky && {
          position: { md: "sticky" },
          // 65px, not 0: the app bar is fixed at that height, so a strip
          // stuck to the viewport top would scroll straight under it.
          top: { md: "65px" },
          zIndex: 2,
        }),
        bgcolor: "background.paper",
        pt: { md: 1 },
        pb: 1.75,
        mb: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* One row on md+: score · bar · chips, centred on one line. The
          score and chip columns are fixed-width so the WC strip (one chip)
          and the SC strip (two) put their bars in the same place. Below
          md: score and chips share the first line, the bar runs full
          width under them. */}
      <Box
        sx={{
          display: "grid",
          alignItems: "center",
          columnGap: { xs: 2, md: 3 },
          rowGap: 1.5,
          gridTemplateColumns: {
            xs: "auto minmax(0, 1fr)",
            md: `${SCORE_COLUMN}px minmax(0, 1fr) ${CHIP_COLUMN}px`,
          },
          gridTemplateAreas: {
            xs: '"score chips" "bar bar"',
            md: '"score bar chips"',
          },
        }}
      >
        <Typography
          aria-label={`Total score ${total} out of ${SCALE}`}
          sx={{
            gridArea: "score",
            fontSize: { xs: 28, sm: 32 },
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {total} / {SCALE}
        </Typography>

        {/* Pass label sits on the tick, above the bar, so the bar and its
            label read as one item. It hangs off the track rather than
            taking a line of its own, so on md+ the track itself sits on the
            row's centre line with the score and chips; below md the bar has
            its own row and reserves the label's height. */}
        <Box sx={{ gridArea: "bar", minWidth: 0, pt: { xs: "20px", md: 0 } }}>
          <Box
            sx={{
              position: "relative",
              height: 6,
              borderRadius: 3,
              bgcolor: "action.hover",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: `${filled}%`,
                borderRadius: 3,
                bgcolor: passed ? "success.main" : "primary.main",
                transition: "width .2s ease, background-color .2s ease",
              }}
            />
            {/* The pass mark is a printed line on the paper form, not a
                gradient stop — keep it a hard tick the eye can land on. */}
            <Box
              sx={{
                position: "absolute",
                left: `${passAt}%`,
                top: -4,
                bottom: -4,
                width: "2px",
                bgcolor: "text.primary",
                opacity: 0.5,
              }}
            />
            <Typography
              sx={{
                position: "absolute",
                bottom: "calc(100% + 6px)",
                left: `${passAt}%`,
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
                fontSize: 11,
                lineHeight: "14px",
                color: "text.secondary",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              Pass {passMark}
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          justifyContent="flex-end"
          alignItems="center"
          sx={{ gridArea: "chips", minWidth: 0 }}
        >
          <Chip
            label={outcome.label}
            colorVariant={outcome.tone}
            chipVariant="filled"
            sx={darkTint(outcome.tone)}
          />
          {badge && (
            <Chip
              label={badge.label}
              colorVariant={badge.tone}
              chipVariant="filled"
              sx={darkTint(badge.tone)}
            />
          )}
        </Stack>
      </Box>

      {note && (
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}
